// Watch progress / Continue Watching — mirrors WatchProgress and
// WatchProgressRepository in the Android app. Persisted in localStorage.
import { useSyncExternalStore } from "react";
import { createStore } from "./store";

const store = createStore("poku.progress", []);

export function progressKey(type, tmdbId, season = null, episode = null) {
  if (type === "EPISODE") return `EPISODE:${tmdbId}:${season || 0}:${episode || 0}`;
  return `${type}:${tmdbId}`;
}

function fractionOf(p) {
  if (p.completed) return 1;
  if (!p.durationSeconds || p.durationSeconds <= 0) return 0;
  return Math.min(1, Math.max(0, p.positionSeconds / p.durationSeconds));
}

export function getProgress(type, tmdbId, season = null, episode = null) {
  const key = progressKey(type, tmdbId, season, episode);
  return store.getSnapshot().find((p) => progressKey(p.contentType, p.tmdbId, p.season, p.episode) === key) || null;
}

export function saveProgress(progress) {
  const key = progressKey(progress.contentType, progress.tmdbId, progress.season, progress.episode);
  store.update((items) => {
    const rest = items.filter((p) => progressKey(p.contentType, p.tmdbId, p.season, p.episode) !== key);
    return [progress, ...rest];
  });
  pushToServer();
}

export function getAllProgress() {
  return store.getSnapshot();
}

// Adopt the server's merged copy (sign-in reconciliation / periodic pull).
// Newest updatedAtMillis wins per key so a fresher local edit is never lost.
export function mergeFromServer(items) {
  if (!Array.isArray(items) || !items.length) return;
  store.update((local) => {
    const byKey = new Map(local.map((p) => [progressKey(p.contentType, p.tmdbId, p.season, p.episode), p]));
    for (const srv of items) {
      if (!srv || !srv.contentType || !srv.tmdbId) continue;
      const k = progressKey(srv.contentType, srv.tmdbId, srv.season, srv.episode);
      const cur = byKey.get(k);
      if (!cur || (srv.updatedAtMillis || 0) >= (cur.updatedAtMillis || 0)) byKey.set(k, srv);
    }
    return [...byKey.values()];
  });
}

// Fire-and-forget push to the account (debounced; skipped when signed out).
// A short debounce coalesces the timeupdate-driven saves into one request.
let pushTimer = null;
function pushToServer() {
  hookFlushOnExit();
  import("../api/auth.js").then(({ getAccount, syncNow }) => {
    if (!getAccount()?.token) return;
    if (pushTimer) clearTimeout(pushTimer);
    pushTimer = setTimeout(() => {
      pushTimer = null;
      syncNow(false).catch(() => {});
    }, 2000);
  }).catch(() => {});
}

// The debounced push can die with the tab: a user who watches a minute and
// closes would lose the tail of their progress. Flush immediately (keepalive
// fetch survives navigation/close) when the tab hides or unloads.
let flushHooked = false;
function hookFlushOnExit() {
  if (flushHooked || typeof window === "undefined") return;
  flushHooked = true;
  const flush = () => {
    if (pushTimer) {
      clearTimeout(pushTimer);
      pushTimer = null;
    }
    Promise.all([
      import("../api/auth.js"),
      import("./watchlist.js"),
    ])
      .then(([{ getAccount }, { getAllWatchlist }]) => {
        const acc = getAccount();
        if (!acc?.token) return;
        const body = JSON.stringify({ progress: store.getSnapshot(), watchlist: getAllWatchlist() });
        fetch("/api/auth/sync", {
          method: "POST",
          keepalive: true,
          headers: { "content-type": "application/json", authorization: `Bearer ${acc.token}` },
          body,
        }).catch(() => {});
      })
      .catch(() => {});
  };
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}

export function clearProgress(type, tmdbId, season = null, episode = null) {
  const key = progressKey(type, tmdbId, season, episode);
  store.update((items) =>
    items.filter((p) => progressKey(p.contentType, p.tmdbId, p.season, p.episode) !== key),
  );
}

export function useProgress() {
  return useSyncExternalStore(store.subscribe, store.getSnapshot);
}

// Continue Watching items: unfinished, at least 2 seconds in, and under 95% done
// (mirrors HomeViewModel's filter).
export function continueWatchingItems(all) {
  return all.filter(
    (p) =>
      !p.completed &&
      p.positionSeconds >= 2.0 &&
      (p.durationSeconds <= 0 || fractionOf(p) < 0.95),
  );
}

export function formatPosition(seconds) {
  if (!seconds || seconds <= 0) return null;
  const s = Math.floor(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = m.toString().padStart(2, "0");
  const ss = sec.toString().padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}